import { action, computed, makeObservable, observable } from "mobx";

import { AuditFilters } from "@/queries/identity/useIdentityAuditEvents";

export const AUDIT_FILTER_PARAM_KEYS = [
  "audit_event_type",
  "audit_source",
  "audit_q",
  "audit_from",
  "audit_to",
] as const;

const filterSignature = (filters: AuditFilters): string =>
  JSON.stringify({
    event_types: [...(filters.event_types ?? [])].sort(),
    source: filters.source ?? "",
    q: filters.q ?? "",
    from: filters.from ?? "",
    to: filters.to ?? "",
  });

export const areAuditFiltersEqual = (left: AuditFilters, right: AuditFilters): boolean =>
  filterSignature(left) === filterSignature(right);

export const readAuditFiltersFromSearchParams = (params: URLSearchParams): AuditFilters => {
  const eventTypes = params.getAll("audit_event_type").filter(Boolean);
  return {
    event_types: eventTypes.length > 0 ? eventTypes : undefined,
    source: params.get("audit_source") || undefined,
    q: params.get("audit_q") || undefined,
    from: params.get("audit_from") || undefined,
    to: params.get("audit_to") || undefined,
  };
};

export const writeAuditFiltersToSearchParams = (
  prev: URLSearchParams,
  filters: AuditFilters,
): URLSearchParams => {
  const next = new URLSearchParams(prev);
  AUDIT_FILTER_PARAM_KEYS.forEach(key => next.delete(key));
  (filters.event_types ?? []).forEach(eventType => next.append("audit_event_type", eventType));
  if (filters.source) {
    next.set("audit_source", filters.source);
  }
  if (filters.q) {
    next.set("audit_q", filters.q);
  }
  if (filters.from) {
    next.set("audit_from", filters.from);
  }
  if (filters.to) {
    next.set("audit_to", filters.to);
  }
  return next;
};

export class AuditFilterStore {
  @observable.shallow private _eventTypes: string[] = [];
  @observable private _source: string | undefined;
  @observable private _q: string | undefined;
  @observable private _from: string | undefined;
  @observable private _to: string | undefined;

  constructor() {
    makeObservable(this);
  }

  @computed get filters(): AuditFilters {
    return {
      event_types: this._eventTypes.length > 0 ? [...this._eventTypes] : undefined,
      source: this._source,
      q: this._q,
      from: this._from,
      to: this._to,
    };
  }

  @computed get isAnyActive(): boolean {
    return Boolean(this._eventTypes.length || this._source || this._q || this._from || this._to);
  }

  @computed get activeFilterCount(): number {
    let count = this._eventTypes.length;
    if (this._source) {
      count += 1;
    }
    if (this._from || this._to) {
      count += 1;
    }
    return count;
  }

  @action setEventTypes(eventTypes: string[]) {
    this._eventTypes = eventTypes;
  }

  @action setSource(source: string | null) {
    this._source = source ?? undefined;
  }

  @action setQuery(q: string | null) {
    const trimmed = q?.trim();
    this._q = trimmed ? trimmed : undefined;
  }

  @action setTimeRange(from: string | null, to: string | null) {
    this._from = from ?? undefined;
    this._to = to ?? undefined;
  }

  @action reset() {
    this._eventTypes = [];
    this._source = undefined;
    this._q = undefined;
    this._from = undefined;
    this._to = undefined;
  }

  @action applyFromURL(filters: AuditFilters) {
    this._eventTypes = [...(filters.event_types ?? [])];
    this._source = filters.source;
    this._q = filters.q;
    this._from = filters.from;
    this._to = filters.to;
  }
}
